import API from '../api';
import * as actions from '../constants';

/**
 * Action to set an entity error
 */
const generateShowError = (name): any =>
  (error): any => ({
    error,
    type: `SHOW_${name.toUpperCase()}_ERROR`,
  });

/**
 * Action to clear entity errors
 */
const generateClearErrors = (name): any =>
  (): any => ({
    type: `CLEAR_${name.toUpperCase()}_ERRORS`,
  });

/**
 * Action to toggle the entity filter
 */
const generateFilter = (name): any =>
  (): any => ({
    type: `FILTER_${name.toUpperCase()}`,
  });

/**
 * Action to mark a fetch entities request as sent
 */
const requestGetPlural =
  (name): any => ({
    type: `${actions.REQUEST}_${name.toUpperCase()}`,
  });

/**
 * Action to mark a fetch entities request as received
 * @param {String|Boolean} error
 * @param {Array} data
 */
const receiveGetPlural =
  (name, error, data, date): any => ({
    error,
    data,
    date,
    type: `${actions.RECEIVE}_${name.toUpperCase()}`,
  });

/**
 * Fetch entities
 * @return {Promise}
 */
const generateGetPlural = (name, url): any =>
  (date = new Date()) => (
    (dispatch) => {
      dispatch(requestGetPlural(name));
      return API.request(`/${url}`)
      .then(response => response.json())
      .then((response) => {
        dispatch(receiveGetPlural(name, response.error, response.data, date));
        return response;
      });
    }
  );

/**
 * Action to mark a fetch all entities request as sent
 */
const requestGetPluralAll =
  (name): any => ({
    type: `${actions.REQUEST}_${name.toUpperCase()}_ALL`,
  });

/**
 * Action to mark a fetch all entities request as received
 * @param {String|Boolean} error
 * @param {Array} data
 */
const receiveGetPluralAll =
  (name, error, data, date): any => ({
    error,
    data,
    date,
    type: `${actions.RECEIVE}_${name.toUpperCase()}_ALL`,
  });

/**
 * Fetch all entities
 * @return {Promise}
 */
const generateGetPluralAll = (name, url): any =>
  (date = new Date()) => (
    (dispatch) => {
      dispatch(requestGetPluralAll(name));
      return API.request(`/${url}?all=true`)
      .then(response => response.json())
      .then((response) => {
        dispatch(receiveGetPluralAll(name, response.error, response.data, date));
        return response;
      });
    }
  );

/**
 * Action to mark a fetch entity request as sent
 * @param {Number} id
 */
const requestGetSingular =
  (name, id): any => ({
    id,
    type: `${actions.REQUEST}_${name.toUpperCase()}`,
  });

/**
 * Action to mark a fetch entity request as received
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
const receiveGetSingular =
  (name, id, error, data, date): any => ({
    id,
    error,
    data,
    date,
    type: `${actions.RECEIVE}_${name.toUpperCase()}`,
  });

/**
 * Fetch an entity
 * @param {Number} id
 * @return {Promise}
 */
const generateGetSingular = (nameSingular, url): any =>
  (id, date = new Date()) => (
    (dispatch) => {
      dispatch(requestGetSingular(nameSingular, id));
      return API.request(`/${url}/${id}`)
      .then(response => response.json())
      .then((response) => {
        dispatch(receiveGetSingular(nameSingular, id, response.error, response.data, date));
        return response;
      });
    }
  );

/**
 * Action to mark an add entity request as sent
 */
const requestAdd = (name): any => ({
  type: `${actions.REQUEST_ADD}_${name.toUpperCase()}`,
});

/**
 * Action to mark an add entity request as received
 * @param {String|Boolean} error
 * @param {Object} data
 */
const receiveAdd = (name, error, data): any => ({
  error,
  data,
  type: `${actions.RECEIVE_ADD}_${name.toUpperCase()}`,
});

/**
 * Add an entity
 * @param {Object} fields
 * @return {Promise}
 */
const generateAdd = (nameSingular, url): any =>
  fields => (
    (dispatch) => {
      dispatch(requestAdd(nameSingular));
      return API.request(`/${url}`, 'POST', fields)
      .then(response => response.json())
      .then((response) => {
        dispatch(receiveAdd(nameSingular, response.error, response.data));
        return response;
      });
    }
  );

/**
 * Action to mark an update entity request as received
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
const requestUpdate = (name, id): any => ({
  id,
  type: `${actions.REQUEST_UPDATE}_${name.toUpperCase()}`,
});

/**
 * Action to mark an update entity request as received
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
const receiveUpdate = (name, id, error, data): any => ({
  id,
  error,
  data,
  type: `${actions.RECEIVE_UPDATE}_${name.toUpperCase()}`,
});

/**
 * Update an entity
 * @param {Number} id
 * @param {Object} fields
 * @return {Promise}
 */
const generateUpdate = (nameSingular, url): any =>
  (id, fields) => (
    (dispatch) => {
      dispatch(requestUpdate(nameSingular, id));
      return API.request(`/${url}/${id}`, 'POST', fields)
      .then(response => response.json())
      .then((response) => {
        dispatch(receiveUpdate(nameSingular, id, response.error, response.data));
        return response;
      });
    }
  );

/**
 * Action to mark a remove entity request as sent
 * @param {Number} id
 */
const requestRemove = (name, id): any => ({
  id,
  type: `${actions.REQUEST_REMOVE}_${name.toUpperCase()}`,
});

/**
 * Action to mark a remove entity request as sent
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
const receiveRemove = (name, id, error, data): any => ({
  id,
  error,
  data,
  type: `${actions.RECEIVE_REMOVE}_${name.toUpperCase()}`,
});

/**
 * Remove an entity
 * @param {Number} id
 * @return {Promise}
 */
const generateRemove = (nameSingular, url): any =>
  id => (
    (dispatch) => {
      dispatch(requestRemove(nameSingular, id));
      return API.request(`/${url}/${id}`, 'DELETE')
      .then(response => response.json())
      .then((response) => {
        dispatch(receiveRemove(nameSingular, id, response.error, response.data));
        return response;
      });
    }
  );

export default (nameSingular,
namePlural,
url = namePlural,
functionSingular = nameSingular,
functionPlural = namePlural) => {
  const functionNameSingular = functionSingular.charAt(0).toUpperCase() +
    functionSingular.substring(1);
  const functionNamePlural = functionPlural.charAt(0).toUpperCase() + functionPlural.substring(1);
  return {
    showError: generateShowError(nameSingular),
    clearErrors: generateClearErrors(nameSingular),
    [`filter${functionNamePlural}`]: generateFilter(namePlural),
    [`get${functionNamePlural}`]: generateGetPlural(namePlural, url),
    [`getAll${functionNamePlural}`]: generateGetPluralAll(namePlural, url),
    [`get${functionNameSingular}`]: generateGetSingular(nameSingular, url),
    [`add${functionNameSingular}`]: generateAdd(nameSingular, url),
    [`update${functionNameSingular}`]: generateUpdate(nameSingular, url),
    [`remove${functionNameSingular}`]: generateRemove(nameSingular, url),

    [`requestGet${functionNamePlural}`]: () => requestGetPlural(namePlural),
    [`receiveGet${functionNamePlural}`]:
      (error, data, date) => receiveGetPlural(namePlural, error, data, date),
    [`requestGetAll${functionNamePlural}`]: () => requestGetPluralAll(namePlural),
    [`receiveGetAll${functionNamePlural}`]:
      (error, data, date) => receiveGetPluralAll(namePlural, error, data, date),
    [`requestGet${functionNameSingular}`]: id => requestGetSingular(nameSingular, id),
    [`receiveGet${functionNameSingular}`]:
      (id, error, data, date) => receiveGetSingular(nameSingular, id, error, data, date),
    [`requestAdd${functionNameSingular}`]: () => requestAdd(nameSingular),
    [`receiveAdd${functionNameSingular}`]: (error, data) => receiveAdd(nameSingular, error, data),
    [`requestUpdate${functionNameSingular}`]: id => requestUpdate(nameSingular, id),
    [`receiveUpdate${functionNameSingular}`]:
      (id, error, data) => receiveUpdate(nameSingular, id, error, data),
    [`requestRemove${functionNameSingular}`]: id => requestRemove(nameSingular, id),
    [`receiveRemove${functionNameSingular}`]:
      (id, error, data) => receiveRemove(nameSingular, id, error, data),
  };
};
